import React from 'react';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import type {SxProps, Theme} from '@mui/material/styles';
import {PickersDay, PickersDayProps, pickersDayClasses} from '@mui/x-date-pickers/PickersDay';
import type {Moment} from 'moment';
import type {LocationAvailabilityDay} from '../../../types/LocationCapacitySchedule';

/** Calendar days keyed by YYYY-MM-DD. */
export type AvailabilityByDate = Record<string, LocationAvailabilityDay>;

// Phones keep smaller days so the mobile picker dialog still fits.
const DAY_SIZE = {xs: 40, sm: 58};
const DAY_CELL = {xs: 44, sm: 62};

export const AVAILABILITY_COLORS = {
  fullFill: '#eeeeee',
  fullText: '#9e9e9e',
  selected: '#bbdefb',
  range: '#e3f2fd',
};

export const AvailableDot = styled('span')(({theme}) => ({
  display: 'inline-block',
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
}));

export const SpacesLeftPill = styled('span')(({theme}) => ({
  display: 'inline-block',
  minWidth: 20,
  height: 17,
  padding: '0 5px',
  boxSizing: 'border-box',
  borderRadius: 9,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '17px',
  textAlign: 'center',
}));

// Notification-style corner badge; the white ring separates it from the day's circle. Kept within the day's
// 2px margin because the calendar clips anything past it in the last column. `wide` ("99+") gets a smaller
// font so it stays no wider than a two-digit count and clear of the date.
const CornerSpacesLeftPill = styled(SpacesLeftPill, {
  shouldForwardProp: (prop) => prop !== 'wide',
})<{wide: boolean}>(({theme, wide}) => ({
  position: 'absolute',
  top: -3,
  right: -1,
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  ...(wide && {padding: '0 3px', fontSize: 10, letterSpacing: '-0.02em'}),
  [theme.breakpoints.down('sm')]: {
    minWidth: 16,
    height: 14,
    padding: wide ? '0 2px' : '0 4px',
    fontSize: wide ? 8 : 10,
    lineHeight: '14px',
  },
}));

// Pinned under the centered date so the corner badge above never reaches the number.
const Marker = styled('span')(({theme}) => ({
  position: 'absolute',
  left: '50%',
  bottom: 7,
  transform: 'translateX(-50%)',
  display: 'flex',
  [theme.breakpoints.down('sm')]: {bottom: 3},
}));

export const availabilityDateKey = (day: Moment): string => day.format('YYYY-MM-DD');

export const formatSpacesLeft = (spaces: number): string => (spaces > 99 ? '99+' : String(spaces));

const availabilityDescription = (availability: LocationAvailabilityDay): string => {
  switch (availability.status) {
    case 'FULL':
      return 'no spaces available';
    case 'SCHEDULED': {
      const spaces = availability.spacesAvailable ?? 0;
      return `${spaces} ${spaces === 1 ? 'space' : 'spaces'} left`;
    }
    default:
      return 'available';
  }
};

/** Makes room in the date picker's calendar for the larger days. */
export const availabilityCalendarLayoutSx: SxProps<Theme> = {
  '& .MuiDateCalendar-root': {height: 'auto', maxHeight: 'none', width: 'auto', minWidth: 320},
  '& .MuiPickersCalendarHeader-label': {fontSize: {sm: '1.25rem'}},
  '& .MuiDayCalendar-weekDayLabel': {width: DAY_SIZE, fontSize: {sm: '1rem'}},
  // MUI renders the blank cells before the 1st without the day slot's sx, so size them here.
  '& .MuiPickersDay-hiddenDaySpacingFiller': {width: DAY_SIZE, height: DAY_SIZE},
  '& .MuiDayCalendar-slideTransition': {minHeight: {xs: DAY_CELL.xs * 6, sm: DAY_CELL.sm * 6}},
};

const baseDaySx: SxProps<Theme> = {
  width: DAY_SIZE,
  height: DAY_SIZE,
  fontSize: {xs: '0.9rem', sm: '1.15rem'},
  lineHeight: 1,
  // Inset outline so today's marker doesn't shift the day's content.
  [`&.${pickersDayClasses.today}, &.${pickersDayClasses.today}:not(.${pickersDayClasses.selected})`]: {
    border: 'none',
    boxShadow: (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}`,
  },
  [`&.${pickersDayClasses.selected}, &.${pickersDayClasses.selected}:hover, &.${pickersDayClasses.selected}:focus`]: {
    backgroundColor: AVAILABILITY_COLORS.selected,
    color: 'text.primary',
  },
};

// The booking's From and To days; the picker's own value already gets the selected style above.
const rangeEndpointDaySx: SxProps<Theme> = {
  [`&:not(.${pickersDayClasses.selected}), &:not(.${pickersDayClasses.selected}):hover, &:not(.${pickersDayClasses.selected}):focus, &.${pickersDayClasses.disabled}:not(.${pickersDayClasses.selected})`]: {
    backgroundColor: AVAILABILITY_COLORS.selected,
    color: 'text.primary',
  },
};

// Days between From and To let the band behind them show, keeping the hover and focus fill.
const rangeMiddleDaySx: SxProps<Theme> = {
  [`&:not(.${pickersDayClasses.selected}):not(:hover):not(:focus)`]: {backgroundColor: 'transparent'},
};

const BAND_RADIUS = {xs: `${DAY_SIZE.xs / 2}px`, sm: `${DAY_SIZE.sm / 2}px`};

// Spans the whole cell, the day's side margins included, so neighboring days' bands join into one bar.
const rangeBandSx = (roundStart: boolean, roundEnd: boolean): SxProps<Theme> => ({
  display: 'flex',
  backgroundColor: AVAILABILITY_COLORS.range,
  ...(roundStart && {borderTopLeftRadius: BAND_RADIUS, borderBottomLeftRadius: BAND_RADIUS}),
  ...(roundEnd && {borderTopRightRadius: BAND_RADIUS, borderBottomRightRadius: BAND_RADIUS}),
});

const fullDaySx: SxProps<Theme> = {
  [`&, &.${pickersDayClasses.disabled}:not(.${pickersDayClasses.selected})`]: {
    backgroundColor: AVAILABILITY_COLORS.fullFill,
    color: AVAILABILITY_COLORS.fullText,
    textDecoration: 'line-through',
  },
};

/** The booking's selected days as inclusive YYYY-MM-DD keys. */
export interface SelectedDateRange {
  start: string;
  end: string;
}

export interface AvailabilityPickersDayProps extends PickersDayProps<Moment> {
  availability?: AvailabilityByDate | null;
  selectedRange?: SelectedDateRange | null;
}

/**
 * Calendar day marking the location's availability (#3271): a green dot when open, a primary-colored corner badge
 * with the spaces left under the supplier's schedule, or grayed out and struck through when full. The booking's
 * From–To range is a rounded light blue band with the From and To days filled in; full days in it stay gray. The
 * calendar's legend explains the markers.
 */
const AvailabilityPickersDay = ({availability, selectedRange, ...dayProps}: AvailabilityPickersDayProps) => {
  const {day, outsideCurrentMonth, disabled, sx} = dayProps;
  const extraSx = Array.isArray(sx) ? sx : [sx];
  if (outsideCurrentMonth) {
    return <PickersDay {...dayProps}/>;
  }

  const dayKey = availabilityDateKey(day);
  const dayAvailability = availability?.[dayKey];
  // Days disabled for another reason (e.g. in the past) get no marker.
  const shown = dayAvailability && (!disabled || dayAvailability.status === 'FULL') ? dayAvailability : undefined;
  const isFull = shown?.status === 'FULL';
  const inRange = !!selectedRange && dayKey >= selectedRange.start && dayKey <= selectedRange.end;
  const isRangeStart = inRange && dayKey === selectedRange.start;
  const isRangeEnd = inRange && dayKey === selectedRange.end;

  let marker: React.ReactNode = null;
  if (shown?.status === 'AVAILABLE') {
    marker = <AvailableDot/>;
  }
  const spacesLeftLabel = shown?.status === 'SCHEDULED' ? formatSpacesLeft(shown.spacesAvailable ?? 0) : null;

  let daySx: SxProps<Theme> | false = false;
  if (inRange && !isFull) {
    daySx = isRangeStart || isRangeEnd ? rangeEndpointDaySx : rangeMiddleDaySx;
  }

  const pickersDay = (
    <PickersDay
      {...dayProps}
      {...(shown && {
        'aria-label': `${day.format('LL')}, ${availabilityDescription(shown)}`,
        'data-availability': shown.status,
      })}
      {...(inRange && {'data-in-range': true})}
      {...((isRangeStart || isRangeEnd) && {'data-range-endpoint': true})}
      sx={[baseDaySx, daySx, isFull && fullDaySx, ...extraSx]}
    >
      <Box component="span" sx={{fontWeight: 500}}>{day.format('D')}</Box>
      {marker && <Marker>{marker}</Marker>}
      {spacesLeftLabel && <CornerSpacesLeftPill wide={spacesLeftLabel.length > 2}>{spacesLeftLabel}</CornerSpacesLeftPill>}
    </PickersDay>
  );
  if (!inRange) {
    return pickersDay;
  }

  // The band is rounded where the range, a week row or the month starts and ends.
  const roundStart = isRangeStart || day.isSame(day.clone().startOf('week'), 'day') || day.date() === 1;
  const roundEnd = isRangeEnd || day.isSame(day.clone().endOf('week'), 'day')
    || day.isSame(day.clone().endOf('month'), 'day');
  return (
    <Box role="presentation" data-range-band sx={rangeBandSx(roundStart, roundEnd)}>
      {pickersDay}
    </Box>
  );
};

export default AvailabilityPickersDay;
